import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { Country, StateProvince } from 'types/Country';
import * as equipmentService from '../../services/equipmentService';
import * as featureService from '../../services/featureService';
import { FuelType } from 'types/FuelType';
import { Truck } from 'types/Truck';
import { BusinessPartner } from 'types/BusinessPartner';
import { SnackBarConfig } from 'types/SnackBarConfig';
import { TmsCompany } from 'types/TmsCompany';
import { Trailer } from 'types/Trailer';
import { TrailerType } from 'types/TrailerType';
import { TrailerDoorType } from 'types/TrailerDoorType';
import { EquipmentMake } from 'types/EquipmentMake';

export interface AppState {
  tmsCompany?: TmsCompany;
  loading?: boolean;
  showSnackBar: boolean;
  stateList?: StateProvince[];
  features?: string[];
  snackBarConfig: SnackBarConfig;
  countries?: Country[];
  fuelTypes?: FuelType[];
  trailerTypes?: TrailerType[];
  trailerDoorTypes?: TrailerDoorType[];
  trailerMakes?: EquipmentMake[];
  trucks?: Truck[];
  trailers?: Trailer[];
  businessPartners?: BusinessPartner[];
}

const initialState: AppState = {
  tmsCompany: undefined,
  loading: false,
  showSnackBar: false,
  stateList: undefined,
  features: undefined,
  snackBarConfig: { message: '', type: undefined },
  countries: undefined,
  fuelTypes: undefined,
  trailerTypes: undefined,
  trailerDoorTypes: undefined,
  trailerMakes: undefined,
  trucks: undefined,
  trailers: undefined,
  businessPartners: undefined,
};

export const getFeatures = createAsyncThunk('app/getFeatures', async () => {
  const { data } = await featureService.getFeatures();
  return data;
});

export const getTmsCompany = createAsyncThunk('app/getTmsCompany', async (_, thunkApi) => {
  const { data } = await equipmentService.getTmsCompany();
  return data;
});

export const getFuelTypes = createAsyncThunk('app/getFuelTypes', async () => {
  const { data } = await equipmentService.getFuelTypes();
  return data;
});

export const getCountries = createAsyncThunk('app/getCountries', async () => {
  const { data } = await equipmentService.getCountries();
  return data;
});

export const getTrucks = createAsyncThunk('app/getTrucks', async () => {
  const { data } = await equipmentService.getTrucks();
  return data;
});

export const getTrailerTypes = createAsyncThunk('app/getTrailerTypes', async () => {
  const { data } = await equipmentService.getTrailerTypes();
  return data;
});

export const getTrailerDoorTypes = createAsyncThunk('app/getTrailerDoorTypes', async () => {
  const { data } = await equipmentService.getTrailerDoorTypes();
  return data;
});

export const getTrailerMakes = createAsyncThunk('app/getTrailerMakes', async () => {
  const { data } = await equipmentService.getEquipmentMakes('trailer');
  return data;
});

export const getTrailers = createAsyncThunk('app/getTrailers', async () => {
  const { data } = await equipmentService.getTrailers();
  return data;
});

export const getBusinessPartners = createAsyncThunk('app/getBusinessPartners', async () => {
  const { data } = await equipmentService.getBusinessPartners();
  return data;
});

export const initData = createAsyncThunk('app/initData', async (_, thunkApi) => {
  thunkApi.dispatch(getFeatures());
  thunkApi.dispatch(getFuelTypes());
  thunkApi.dispatch(getTrailerTypes());
  thunkApi.dispatch(getTrailerDoorTypes());
  thunkApi.dispatch(getTrailerMakes());
  thunkApi.dispatch(getCountries());
  thunkApi.dispatch(getTrucks());
  thunkApi.dispatch(getBusinessPartners());
  thunkApi.dispatch(getTmsCompany());
  thunkApi.dispatch(getTrailers());
});

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setLoading: (state: AppState, action: PayloadAction<boolean | undefined>) => {
      state.loading = action.payload;
    },
    setShowSnackbar: (state: AppState, action: PayloadAction<boolean>) => {
      state.showSnackBar = action.payload;
    },
    setSnackbarConfig: (state: AppState, action: PayloadAction<SnackBarConfig>) => {
      state.snackBarConfig = action.payload;
    },
    setAndShowSnackbar: (state: AppState, action: PayloadAction<SnackBarConfig>) => {
      state.snackBarConfig = action.payload;
      state.showSnackBar = true;
    },
    updateTruckInList: (state: AppState, action: PayloadAction<Truck>) => {
      const truck = { ...action.payload, equipmentType: 'truck' };
      state.trucks = state.trucks?.map((t) => {
        if (t.id === truck.id) {
          return truck;
        }
        return t;
      });
    },
    addTruckToList: (state: AppState, action: PayloadAction<Truck>) => {
      const truck = { ...action.payload, equipmentType: 'truck' };
      state.trucks = [truck, ...(state.trucks as Truck[])];
    },
    updateTrailerInList: (state: AppState, action: PayloadAction<Trailer>) => {
      const types = state.trailerTypes;
      const doorTypes = state.trailerDoorTypes;
      const type = types?.find((ty) => ty.id === action.payload.trailerTypeId);
      const doorType = doorTypes?.find((d) => d.id === action.payload.trailerDoorTypeId);
      const trailer = { ...action.payload, trailerTypeName: type?.name, trailerDoorTypeName: doorType?.name, equipmentType: 'trailer' };
      state.trailers = state.trailers?.map((t) => {
        if (t.id === trailer.id) {
          return trailer;
        }
        return t;
      });
    },
    addTrailerToList: (state: AppState, action: PayloadAction<Trailer>) => {
      const types = state.trailerTypes;
      const doorTypes = state.trailerDoorTypes;
      const type = types?.find((ty) => ty.id === action.payload.trailerTypeId);
      const doorType = doorTypes?.find((d) => d.id === action.payload.trailerDoorTypeId);
      const trailer = { ...action.payload, trailerTypeName: type?.name, trailerDoorTypeName: doorType?.name, equipmentType: 'trailer' };
      state.trailers = [trailer, ...(state.trailers as Trailer[])];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeatures.fulfilled, (state, action) => {
        state.features = action.payload;
      })
      .addCase(getFeatures.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Features',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTmsCompany.fulfilled, (state, action) => {
        state.tmsCompany = action.payload;
      })
      .addCase(getTmsCompany.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading TMS Company',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getFuelTypes.fulfilled, (state, action) => {
        state.fuelTypes = action.payload;
      })
      .addCase(getFuelTypes.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Fuel Types',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTrailerTypes.fulfilled, (state, action) => {
        state.trailerTypes = action.payload;
      })
      .addCase(getTrailerTypes.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Trailer Types',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTrailerDoorTypes.fulfilled, (state, action) => {
        state.trailerDoorTypes = action.payload;
      })
      .addCase(getTrailerDoorTypes.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Trailer Door Types',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTrailerMakes.fulfilled, (state, action) => {
        state.trailerMakes = action.payload;
      })
      .addCase(getTrailerMakes.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Trailer Makes',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
        state.stateList = action.payload[0].stateProvinces;
      })
      .addCase(getCountries.rejected, (state, _action) => {
        state.snackBarConfig = {
          message: 'Error Loading Countries',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTrucks.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(getTrucks.fulfilled, (state, action) => {
        state.loading = false;
        const trucks = action.payload.map((t) => {
          return { ...t, equipmentType: 'truck' };
        });
        state.trucks = trucks;
      })
      .addCase(getTrucks.rejected, (state, _action) => {
        state.loading = false;
        state.snackBarConfig = {
          message: 'Error Loading Trucks',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getTrailers.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(getTrailers.fulfilled, (state, action) => {
        state.loading = false;
        const types = state.trailerTypes;
        const doorTypes = state.trailerDoorTypes;
        const trailers = action.payload.map((t) => {
          const type = types?.find((ty) => ty.id === t.trailerTypeId);
          const doorType = doorTypes?.find((d) => d.id === t.trailerDoorTypeId);
          return { ...t, trailerTypeName: type?.name, trailerDoorTypeName: doorType?.name, equipmentType: 'trailer' };
        });
        state.trailers = trailers;
      })
      .addCase(getTrailers.rejected, (state, _action) => {
        state.loading = false;
        state.snackBarConfig = {
          message: 'Error Loading Trailers',
          type: 'error',
        };
        state.showSnackBar = true;
      })
      .addCase(getBusinessPartners.pending, (state, _action) => {
        state.loading = true;
      })
      .addCase(getBusinessPartners.fulfilled, (state, action) => {
        state.loading = false;
        state.businessPartners = action.payload;
      })
      .addCase(getBusinessPartners.rejected, (state, _action) => {
        state.loading = false;
        state.snackBarConfig = {
          message: 'Error Loading Business Partners',
          type: 'error',
        };
        state.showSnackBar = true;
      });
  },
});

export default appSlice.reducer;
export const getLoading = (state: RootState) => state.app.loading;
export const getTruckList = (state: RootState) => state.app.trucks;
export const getTruckFromList = (id: number) => (state: RootState) => state.app.trucks?.find((t) => t.id === id);
export const getTrailerList = (state: RootState) => state.app.trailers;
export const getTrailerFromList = (id: number) => (state: RootState) => state.app.trailers?.find((t) => t.id === id);
export const getSnackbarConfig = (state: RootState) => state.app.snackBarConfig;
export const getShowSnackbar = (state: RootState) => state.app.showSnackBar;
export const getTmsCompanyId = (state: RootState) => state.app.tmsCompany?.id;
export const getStateList = (state: RootState) => state.app.stateList;
export const getFuelTypeList = (state: RootState) => state.app.fuelTypes;
export const getTrailerTypeList = (state: RootState) => state.app.trailerTypes;
export const getTrailerDoorTypeList = (state: RootState) => state.app.trailerDoorTypes;
export const getTrailerMakeList = (state: RootState) => state.app.trailerMakes;
export const filterPartnerByType = (partnerType: number) => (state: any) => {
  return state.app.businessPartners.reduce((a: BusinessPartner[], c: BusinessPartner) => {
    if (c.types) {
      const isItem = c.types.find((t) => t.id === partnerType);
      return isItem ? [...a, c] : a;
    }
    return a;
  }, []);
};
export const {
  setLoading,
  setShowSnackbar,
  setSnackbarConfig,
  setAndShowSnackbar,
  updateTruckInList,
  addTruckToList,
  updateTrailerInList,
  addTrailerToList,
} = appSlice.actions;
